import { useAuth } from "@clerk/vue";
import { computed } from "vue";
import { useRoles } from "./useRoles";

export interface NavigationItem {
  label: string;
  path: string;
  icon: string;
  show: boolean;
  /** Section header label for sidebar grouping */
  section?: string;
  /** Child items for collapsible sections */
  children?: NavigationItem[];
}

export function useNavigation() {
  const auth = useAuth();
  const roles = useRoles();

  const navigationItems = computed(() => {
    const items: NavigationItem[] = [];

    // Show these only when signed in
    if (auth?.userId?.value) {
      if (roles.hasDirectReports.value) {
        items.push({
          label: "My Team",
          path: "/dashboard/team",
          icon: "users",
          show: true,
          section: "Personal",
        });
      }

      items.push({
        label: "My Activity",
        path: "/activity",
        icon: "activity",
        show: true,
        section: "Personal",
      });

      items.push({
        label: "Goals",
        path: "/goals",
        icon: "target",
        show: true,
        section: "Personal",
      });

      // "Standards" = career frameworks (levels + competencies / "rubrics").
      items.push({
        label: "Standards",
        path: "/rubrics",
        icon: "clipboard-list",
        show: true,
        section: "Personal",
      });

      items.push({
        label: "Chat",
        path: "/chat",
        icon: "message-circle",
        show: true,
        section: "Personal",
      });

      // Pulses (creator hub) — admins and managers only. ICs can't author
      // pulses, so the nav entry would dead-end them at the role guard.
      if (roles.canManage.value) {
        items.push({
          label: "Pulses",
          path: "/pulses",
          icon: "heart-pulse",
          show: true,
          section: "Personal",
        });
      }

      // Insights (company-wide health dashboard) — admins only.
      if (roles.canAdmin.value) {
        items.push({
          label: "Insights",
          path: "/dashboard/company",
          icon: "bar-chart-3",
          show: true,
        });
      }

      // Settings: visible to managers (with direct reports) and/or admins
      if (roles.hasDirectReports.value || roles.canAdmin.value) {
        const settingsChildren: NavigationItem[] = [];

        if (roles.hasDirectReports.value) {
          settingsChildren.push({
            label: "Channel Analysis",
            path: "/dashboard/team/channel-analysis",
            icon: "hash",
            show: true,
          });
        }

        if (roles.canAdmin.value) {
          settingsChildren.push(
            {
              label: "Organization Chart",
              path: "/admin/hierarchy",
              icon: "building-2",
              show: true,
            },
            {
              label: "Integrations",
              path: "/admin/integrations",
              icon: "plug",
              show: true,
            },
            {
              label: "Org. Context",
              path: "/admin/organizational-context",
              icon: "compass",
              show: true,
            },
            {
              label: "Performance Review Settings",
              path: "/admin/performance-review-settings",
              icon: "star",
              show: true,
            },
            {
              label: "Bulk Reviews",
              path: "/admin/performance-reviews",
              icon: "file-text",
              show: true,
            },
            {
              label: "Invite Team",
              path: "/admin/invite",
              icon: "user-plus",
              show: true,
            },
            {
              label: "Leveling",
              path: "/admin/leveling",
              icon: "layers",
              show: true,
            },
            {
              label: "Rating Scale",
              path: "/admin/rating-scale",
              icon: "sliders-horizontal",
              show: true,
            },
            {
              label: "Billing",
              path: "/admin/billing",
              icon: "credit-card",
              show: true,
            },
            {
              label: "Admin Settings",
              path: "/admin/settings",
              icon: "settings",
              show: true,
            },
          );
        }

        items.push({
          label: "Settings",
          path: "/admin",
          icon: "settings",
          show: true,
          children: settingsChildren,
        });
      }

      // Dev tools - Clerk org admins only (app creators: Jordan, Adam, Barb)
      if (roles.isClerkAdmin.value) {
        items.push({
          label: "Dev",
          path: "/dev",
          icon: "wrench",
          show: true,
          children: [
            {
              label: "Tools",
              path: "/dev-admin",
              icon: "settings",
              show: true,
            },
            {
              label: "Pando Connections",
              path: "/dev-admin/pando-connections",
              icon: "plug",
              show: true,
            },
            {
              label: "Admin Channel Insights",
              path: "/dev/channel-insights",
              icon: "scan-search",
              show: true,
            },
          ],
        });
      }
    }

    return items.filter((item) => item.show);
  });

  return {
    navigationItems,
  };
}
